import { staleTimes, useQueries } from '@platform/api-client';
import type { Operation, ResponseBody, TypedClient } from '@platform/openapi';
import { pickName } from '../catalog/categories.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import type { VariantLabel } from './inventory.ts';
import { chunksOf, MAX_IDS_PER_REQUEST } from './stock-levels.ts';

export type VariantSummary = ResponseBody<Operation<'/admin/catalog/variants', 'get'>>['items'][number];

export const labelsOf = (
  summaries: readonly VariantSummary[],
  locale: string,
): ReadonlyMap<string, VariantLabel> =>
  new Map(
    summaries.map((summary) => [
      summary.variantId,
      {
        sku: summary.sku,
        productName: pickName(summary.productNames, locale, summary.productId),
        productId: summary.productId,
      },
    ]),
  );

export const useVariantLabels = (api: TypedClient, variantIds: readonly string[]) => {
  const { tenantId, locale } = usePageContext();
  const chunks = chunksOf([...new Set(variantIds)].sort(), MAX_IDS_PER_REQUEST);
  const answers = useQueries({
    queries: chunks.map((ids) => ({
      queryKey: queryKeys.variantNames(tenantId, { ids }),
      queryFn: () => api.get('/admin/catalog/variants', { query: { id: ids } }),
      staleTime: staleTimes.catalog,
    })),
  });
  return labelsOf(
    answers.flatMap((answer) => answer.data?.items ?? []),
    locale,
  );
};
